import { useMemo } from 'react';
import { useDUPStatus, useGetProjectsByIdsQuery, usePermitStatus } from '@/features/projects';
import { projectDetailsTransformer } from '@/features/projects/helpers';
import {
  useBooleanDisplayNormalizer,
  useConnectionTo,
  useConnectionType,
  useCountries,
  useDevelopmentStatus,
  useLandRight,
  useTechnologies,
} from '@/hooks';
import {
  transformCapexOpexFromProject,
  transformConnectionInfraFromProject,
  transformGeneralInfoFromProject,
  transformLandRightsFromProject,
  transformPermitsFromProject,
  transformOtherInfoFromProject,
  processTechnologiesWithType,
  transformPortfolioSummaryFromProject,
  transformFinancialHighlightsFromProject,
  transformTeamGovernanceFromProject,
} from '../components/generate-teaser/helpers';
import { useUpgradingTechnology } from '@/features/projects/hooks/use-upgrading-technology';

type ProjectForPpt = {
  generalInformation: any;
  permits: any;
  connectionInfra: any;
  landRights: any;
  capexAndOpex: any;
  otherInfo: any;
  technologies: any;
  portfolioSummary?: any;
  financialHighlights?: any;
  teamGovernance?: any;
  isTopco: boolean;
};

export function useNormalizedProjectsByIds(ids: string[]) {
  const { map: mapTechnology } = useTechnologies();
  const { map: mapPermitStatus } = usePermitStatus();
  const { map: mapConnectionTo } = useConnectionTo();
  const { map: mapConnectionType } = useConnectionType();
  const { map: landRightsmap } = useLandRight();
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const { map: mapCountry } = useCountries();
  const { options: statusOptions } = useDUPStatus();
  const { options: upgradingTechnologyOptions } = useUpgradingTechnology();
  const booleanFormatter = useBooleanDisplayNormalizer();

  const {
    data = [],
    isFetching,
    isError,
    error,
  } = useGetProjectsByIdsQuery(ids, {
    skip: ids.length === 0,
  });

  const projects: ProjectForPpt[] = useMemo(() => {
    return data.map((raw) => {
      const project = projectDetailsTransformer(raw);
      const isTopco = project?.technologies?.some((t: any) => t.type === 'topco') ?? false;

      const generalInformation = !isTopco ? transformGeneralInfoFromProject(project, mapTechnology) : [];
      const technologies = processTechnologiesWithType(project?.technologies, {
        mapDevelopmentStatus,
        booleanToText: booleanFormatter,
        upgradingTechnologyOptions,
      });

      // Secciones transversales
      const permits = !isTopco
        ? transformPermitsFromProject(project, 'ES', mapPermitStatus)
        : { permits: [], comments: null };
      const connectionInfra = !isTopco
        ? transformConnectionInfraFromProject(project, {
            mapConnectionTo,
            mapConnectionType,
            booleanToText: booleanFormatter,
          })
        : [];
      const landRights = !isTopco ? transformLandRightsFromProject(project, { landRightsmap, statusOptions }) : [];
      const capexAndOpex = !isTopco ? transformCapexOpexFromProject(project) : [];
      const otherInfo = !isTopco ? transformOtherInfoFromProject(project) : [];

      // Secciones de topco
      const portfolioSummary = isTopco
        ? transformPortfolioSummaryFromProject(project, mapCountry, mapTechnology)
        : undefined;
      const financialHighlights = isTopco ? transformFinancialHighlightsFromProject(project) : undefined;
      const teamGovernance = isTopco ? transformTeamGovernanceFromProject(project) : undefined;

      return {
        generalInformation,
        permits,
        connectionInfra,
        landRights,
        capexAndOpex,
        otherInfo,
        technologies,
        portfolioSummary,
        financialHighlights,
        teamGovernance,
        isTopco,
      };
    });
  }, [data, mapTechnology]);

  return { projects, isFetching, isError, error };
}
